import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { usePath } from '../../utils/navigation';
import { LayoutGrid, MessageSquare, Zap, CalendarDays, Gamepad2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { subscribeToMessageThreads, isMemberUnpaidOrExpired, getUpcomingEvents } from '../../firebase';
import { useQueryClient } from '@tanstack/react-query';
import { prefetchFeed } from '../../hooks/queries/useFeed';
import { prefetchRoute } from '../../utils/prefetch';
import '../Dashboard/Dashboard.scss';

export const BottomNav: React.FC = () => {
  const queryClient = useQueryClient();
  const path = usePath();
  const [designVersion, setDesignVersion] = useState<'v1' | 'v2'>(() => {
    const saved = localStorage.getItem('acs-design-version');
    if (saved === 'v2' || saved === 'v1') return saved;
    return (path.includes('/members/hub-v2') || path.includes('/feed-v2')) ? 'v2' : 'v1';
  });

  useEffect(() => {
    if (path.includes('/members/hub-v2') || path.includes('/feed-v2')) {
      localStorage.setItem('acs-design-version', 'v2');
      setDesignVersion('v2');
    } else if (path === '/members' || path === '/feed') {
      localStorage.setItem('acs-design-version', 'v1');
      setDesignVersion('v1');
    }
  }, [path]);

  const isV2 = designVersion === 'v2';
  const hubPath = isV2 ? '/members/hub-v2' : '/members';
  const feedPath = isV2 ? '/feed-v2' : '/feed';
  const { profile, user } = useAuth();
  const [hasUnread, setHasUnread] = useState(false);

  useEffect(() => {
    if (!user) {
      setHasUnread(false);
      return;
    }

    const currentMemberId = user.uid;
    const isUnpaid = isMemberUnpaidOrExpired(profile);

    const unsubscribe = subscribeToMessageThreads((threadsList: any[]) => {
      const hasUnreadThreads = threadsList.some((thread) => {
        // Filter based on unpaid status
        if (isUnpaid) {
          const otherMembers = thread.members.filter((m: any) => m.id !== currentMemberId);
          const hasAdmin = otherMembers.some((m: any) => m.isAdmin === true);
          if (!hasAdmin) return false;
        }

        const lastReadTime = thread.lastRead?.[currentMemberId];
        return (!lastReadTime || lastReadTime < thread.updatedAt) && thread.lastMessage;
      });

      setHasUnread(hasUnreadThreads);
    });

    return () => unsubscribe();
  }, [user, profile]);

  const isUnpaid = isMemberUnpaidOrExpired(profile);

  // Hide bottom nav during active gameplay on tables and in tutorial
  const isPlayingAtTable =
    path.startsWith('/play') ||
    (path.startsWith('/arena') && path !== '/arena') ||
    path.startsWith('/game') ||
    path.startsWith('/table') ||
    path.startsWith('/watch') ||
    path.startsWith('/room');
  if (isPlayingAtTable) {
    return null;
  }

  return (
    <nav className="floating-bottom-nav white-theme">
      <NavLink 
        id="hub-tab"
        to={hubPath}
        onMouseEnter={() => prefetchRoute(hubPath)}
        onTouchStart={() => prefetchRoute(hubPath, true)}
        className={({ isActive }) => `nav-item ${isActive || path === '/profile' || path === '/members/hub-v2' || path === '/members' ? 'active' : ''}`}
      >
        <LayoutGrid size={20} strokeWidth={2.5} />
        <span>Hub</span>
      </NavLink>

      <NavLink 
        id="inbox-tab"
        to="/messages"
        onMouseEnter={() => prefetchRoute('/messages')}
        onTouchStart={() => prefetchRoute('/messages', true)}
        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
      >
        <div className="relative flex items-center justify-center">
          <MessageSquare size={20} strokeWidth={2.5} />
          {hasUnread && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#c72f38] rounded-full border border-white shadow-sm" />
          )}
        </div>
        <span>Inbox</span>
      </NavLink>

      {!isUnpaid && (
        <>
          <NavLink 
            id="feed-btn"
            to={feedPath}
            onMouseEnter={() => {
              prefetchFeed(queryClient).catch(() => {});
              prefetchRoute(feedPath);
            }}
            onTouchStart={() => {
              prefetchFeed(queryClient).catch(() => {});
              prefetchRoute(feedPath, true);
            }}
            className={({ isActive }) => `nav-item ${isActive || path === '/feed' || path === '/feed-v2' ? 'active' : ''}`}
          >
            <Zap size={20} strokeWidth={2.5} />
            <span>Feed</span>
          </NavLink>

          <NavLink 
            id="events-tab"
            to="/events"
            onMouseEnter={() => {
              queryClient.prefetchQuery({ queryKey: ['events', 'upcoming'], queryFn: () => getUpcomingEvents(), staleTime: 60 * 1000 }).catch(() => {});
              prefetchRoute('/events');
            }}
            onTouchStart={() => {
              queryClient.prefetchQuery({ queryKey: ['events', 'upcoming'], queryFn: () => getUpcomingEvents(), staleTime: 60 * 1000 }).catch(() => {});
              prefetchRoute('/events', true);
            }}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <CalendarDays size={20} strokeWidth={2.5} />
            <span>Events</span>
          </NavLink>

          <NavLink 
            id="arena-tab"
            to="/arena"
            onMouseEnter={() => prefetchRoute('/arena')}
            onTouchStart={() => prefetchRoute('/arena', true)}
            className={({ isActive }) => `nav-item ${isActive || path.startsWith('/arena') || path.startsWith('/play') ? 'active' : ''}`}
          >
            <Gamepad2 size={20} strokeWidth={2.5} />
            <span>Arena</span>
          </NavLink>
        </>
      )}
    </nav>
  );
};

